import { Check, Copy, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatCurrency } from "../lib/currency";
import { formatIban } from "../lib/billingFormat";
import { useI18n } from "../lib/i18n";

async function copyText(value) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(String(value));
  const field = document.createElement("textarea");
  field.value = String(value); field.style.position = "fixed"; field.style.opacity = "0";
  document.body.appendChild(field); field.select(); document.execCommand("copy"); field.remove();
}

function visualViewportRect() {
  const viewport = window.visualViewport;
  if (!viewport) return null;
  return {
    top: Math.max(0, viewport.offsetTop || 0),
    left: Math.max(0, viewport.offsetLeft || 0),
    width: Math.max(1, viewport.width || window.innerWidth),
    height: Math.max(1, viewport.height || window.innerHeight),
  };
}

function scrollContainerFor(node) {
  let current = node?.parentElement;
  while (current && current !== document.body && current !== document.documentElement) {
    const style = window.getComputedStyle(current);
    if (/(auto|scroll|overlay)/.test(style.overflowY) && current.scrollHeight > current.clientHeight) return current;
    current = current.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

export function PaymentValue({ label, value, copyValue = value }) {
  const { t } = useI18n(); const [copied, setCopied] = useState(false);
  if (!value) return null;
  return <div className="flex items-center gap-2 py-2 border-b last:border-b-0" style={{ borderColor: "var(--pc-border)" }}>
    <span className="min-w-0 flex-1"><span className="block text-[10px] uppercase tracking-[.11em]" style={{ color: "var(--pc-muted)" }}>{label}</span><span className="block text-sm font-semibold mt-0.5 break-words" style={{ overflowWrap: "anywhere" }}>{value}</span></span>
    <button type="button" className="w-10 h-10 rounded-lg grid place-items-center shrink-0 focus-visible:outline-none focus-visible:ring-2" style={{ color: copied ? "var(--pc-score-good)" : "var(--pc-cyan)", background: "var(--pc-cyan-dim)" }} aria-label={copied ? `${t("copied")}: ${label}` : `${t("copy")} ${label}`} onClick={async () => { await copyText(copyValue); setCopied(true); window.setTimeout(() => setCopied(false), 1200); }}>{copied ? <Check size={14} /> : <Copy size={14} />}</button>
  </div>;
}

export function PaymentDetails({ invoice }) {
  const { t, lang } = useI18n(); const payment = invoice?.payment || {};
  return <div className="rounded-xl px-3" style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }} data-testid="monthly-invoice-payment-details">
    <PaymentValue label={t("paymentRecipient")} value={payment.recipient_name} />
    <PaymentValue label="IBAN" value={formatIban(payment.iban)} copyValue={payment.iban} />
    <PaymentValue label="BIC" value={payment.bic} />
    <PaymentValue label={t("bankName")} value={payment.bank_name} />
    <PaymentValue label={t("amount")} value={formatCurrency(payment.amount ?? invoice.total_amount, payment.currency || invoice.currency, lang)} copyValue={Number(payment.amount ?? invoice.total_amount).toFixed(2)} />
    <PaymentValue label={t("paymentReference")} value={payment.reference} />
  </div>;
}

export default function MonthlyInvoicePaymentSheet({ invoice, onClose }) {
  const { t } = useI18n();
  const dialogRef = useRef(null); const closeRef = useRef(onClose); const titleId = useId();
  const [viewport, setViewport] = useState(() => visualViewportRect());
  closeRef.current = onClose;
  useEffect(() => {
    if (!invoice) return undefined;
    const body = document.body; const root = document.documentElement; const previousFocus = document.activeElement;
    const scrollContainer = scrollContainerFor(previousFocus);
    const windowScroll = { x: window.scrollX, y: window.scrollY };
    const containerScroll = { x: scrollContainer.scrollLeft, y: scrollContainer.scrollTop };
    const previous = {
      bodyOverflow: body.style.overflow, rootOverflow: root.style.overflow,
      containerOverflow: scrollContainer.style.overflow,
    };
    if (scrollContainer === root || scrollContainer === body) {
      root.style.overflow = "hidden"; body.style.overflow = "hidden";
    } else scrollContainer.style.overflow = "hidden";
    const dialog = dialogRef.current;
    const focusable = () => Array.from(dialog?.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])") || []).filter((node) => !node.disabled);
    const restorePosition = () => {
      if (window.scrollX !== windowScroll.x || window.scrollY !== windowScroll.y) window.scrollTo(windowScroll.x, windowScroll.y);
      if (scrollContainer !== root && scrollContainer !== body) {
        scrollContainer.scrollLeft = containerScroll.x; scrollContainer.scrollTop = containerScroll.y;
      }
    };
    const focusFrame = window.requestAnimationFrame(() => {
      dialog?.focus({ preventScroll: true });
      restorePosition();
    });
    const preventBackgroundTouch = (event) => {
      if (!dialog?.contains(event.target)) event.preventDefault();
    };
    const updateViewport = () => setViewport(visualViewportRect());
    document.addEventListener("touchmove", preventBackgroundTouch, { passive: false });
    window.visualViewport?.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("scroll", updateViewport);
    window.addEventListener("resize", updateViewport);
    const onKeyDown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); closeRef.current(); return; }
      if (event.key !== "Tab") return;
      const nodes = focusable(); if (!nodes.length) { event.preventDefault(); dialog?.focus(); return; }
      const first = nodes[0]; const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("touchmove", preventBackgroundTouch);
      window.visualViewport?.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
      body.style.overflow = previous.bodyOverflow; root.style.overflow = previous.rootOverflow;
      scrollContainer.style.overflow = previous.containerOverflow;
      restorePosition();
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [invoice]);
  if (!invoice) return null;
  const viewportStyle = viewport ? {
    top: `${viewport.top}px`, left: `${viewport.left}px`, right: "auto", bottom: "auto",
    width: `${viewport.width}px`, height: `${viewport.height}px`,
  } : {};
  return createPortal(<div className="monthly-invoice-payment-sheet flex items-end sm:items-center justify-center sm:p-4" style={{ position: "fixed", zIndex: 1300, ...viewportStyle, background: "rgba(0,0,0,.68)" }} onClick={onClose} data-testid="monthly-invoice-payment-sheet" data-visual-viewport={viewport ? "true" : "false"}>
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="pc-card monthly-invoice-payment-sheet__dialog w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 overflow-y-auto" onClick={(event) => event.stopPropagation()}>
      <header className="flex items-start gap-3 mb-2"><div className="min-w-0 flex-1"><h3 id={titleId} className="font-display font-semibold text-lg">{t("prepareTransfer")}</h3><p className="text-xs mt-1" style={{ color: "var(--pc-muted)" }}>{t("transferPreparationHint")}</p></div><button type="button" className="w-10 h-10 rounded-lg grid place-items-center shrink-0 focus-visible:outline-none focus-visible:ring-2" onClick={onClose} aria-label={t("close")} data-testid="payment-sheet-close"><X size={16} /></button></header>
      <PaymentDetails invoice={invoice} />
    </section>
  </div>, document.body);
}
